import React from 'react';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';

import useLocalStorage, { StorageKey } from 'src/hooks/useLocalStorage';
import { type ArrowPosition, arrowPositionToCss, computeArrowPosition, isArrowPosition, liveDragToCss } from './position';

const LONG_PRESS_MS = 3000;
/** Movement (px) tolerated during the long-press wait; beyond it the press is no longer treated as a hold. */
const MOVE_CANCEL_THRESHOLD_PX = 10;

interface DraggableHandlers {
  onPointerDown: React.PointerEventHandler<HTMLDivElement>;
  onPointerMove: React.PointerEventHandler<HTMLDivElement>;
  onPointerUp: React.PointerEventHandler<HTMLDivElement>;
  onPointerCancel: React.PointerEventHandler<HTMLDivElement>;
  onClick: React.MouseEventHandler<HTMLDivElement>;
  onContextMenu: React.MouseEventHandler<HTMLDivElement>;
}

export interface UseDraggablePositionResult {
  /** Callback ref for the button — accepted by the `ref` prop across React 18/19 type definitions. */
  ref: React.RefCallback<HTMLDivElement>;
  /** CSS positioning declarations to apply, or `undefined` to keep the default placement. */
  positionCss: string | undefined;
  isDragging: boolean;
  handlers: DraggableHandlers;
}

/**
 * Wraps a fixed-position button with long-press-to-drag behaviour: holding the left mouse button (or a
 * long tap) for {@link LONG_PRESS_MS} arms dragging, the button then follows the pointer, and on release
 * its edge-relative anchor is persisted to localStorage so it always re-renders in the chosen spot.
 *
 * @param onActivate the regular click action (a tap that never turned into a drag still triggers it).
 */
export const useDraggablePosition = ({ onActivate }: { onActivate: () => void }): UseDraggablePositionResult => {
  const { loadJSON, saveJSON } = useLocalStorage();

  const elementRef = React.useRef<HTMLDivElement | null>(null);
  const setRef = useCallbackRef<React.RefCallback<HTMLDivElement>>((node) => {
    elementRef.current = node;
  });

  const [saved, setSaved] = React.useState<ArrowPosition | null>(() => {
    const stored = loadJSON<ArrowPosition>(StorageKey.SCROLL_ARROW_POSITION);
    return isArrowPosition(stored) ? stored : null;
  });
  const [dragPos, setDragPos] = React.useState<{ x: number; y: number } | null>(null);

  const longPressTimer = React.useRef<number | null>(null);
  const grabOffset = React.useRef({ x: 0, y: 0 });
  const startPointer = React.useRef({ x: 0, y: 0 });
  const lastPointer = React.useRef({ x: 0, y: 0 });
  const isDraggingRef = React.useRef(false);
  const suppressNextClick = React.useRef(false);
  const activePointerId = React.useRef<number | null>(null);

  const clearLongPress = useCallbackRef(() => {
    if (longPressTimer.current !== null) {
      window.clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  });

  const beginDrag = useCallbackRef(() => {
    const element = elementRef.current;
    if (!element) return;
    const rect = element.getBoundingClientRect();
    grabOffset.current = { x: lastPointer.current.x - rect.left, y: lastPointer.current.y - rect.top };
    isDraggingRef.current = true;
    // The interaction became a drag, so the click that fires on release must not scroll to top.
    suppressNextClick.current = true;
    if (activePointerId.current !== null) {
      try {
        element.setPointerCapture(activePointerId.current);
      } catch {
        // Capture is best-effort; dragging still works while the pointer stays over the button.
      }
    }
    setDragPos({ x: rect.left, y: rect.top });
  });

  const endDrag = useCallbackRef((pointerId: number) => {
    clearLongPress();
    if (!isDraggingRef.current) {
      activePointerId.current = null;
      return;
    }
    isDraggingRef.current = false;
    const element = elementRef.current;
    try {
      element?.releasePointerCapture(pointerId);
    } catch {
      // Ignore: the capture may already have been released.
    }
    if (element) {
      const next = computeArrowPosition(element.getBoundingClientRect(), window.innerWidth, window.innerHeight);
      setSaved(next);
      saveJSON(StorageKey.SCROLL_ARROW_POSITION, next);
    }
    setDragPos(null);
    activePointerId.current = null;
  });

  const onPointerDown = useCallbackRef<React.PointerEventHandler<HTMLDivElement>>((event) => {
    if (event.button !== 0) return;
    suppressNextClick.current = false;
    startPointer.current = { x: event.clientX, y: event.clientY };
    lastPointer.current = { x: event.clientX, y: event.clientY };
    activePointerId.current = event.pointerId;
    clearLongPress();
    longPressTimer.current = window.setTimeout(beginDrag, LONG_PRESS_MS);
  });

  const onPointerMove = useCallbackRef<React.PointerEventHandler<HTMLDivElement>>((event) => {
    lastPointer.current = { x: event.clientX, y: event.clientY };
    if (isDraggingRef.current) {
      event.preventDefault();
      setDragPos({ x: event.clientX - grabOffset.current.x, y: event.clientY - grabOffset.current.y });
      return;
    }
    if (longPressTimer.current === null) return;
    const movedBy = Math.hypot(event.clientX - startPointer.current.x, event.clientY - startPointer.current.y);
    if (movedBy > MOVE_CANCEL_THRESHOLD_PX) {
      clearLongPress();
    }
  });

  const onPointerUp = useCallbackRef<React.PointerEventHandler<HTMLDivElement>>((event) => {
    endDrag(event.pointerId);
  });

  const onPointerCancel = useCallbackRef<React.PointerEventHandler<HTMLDivElement>>((event) => {
    endDrag(event.pointerId);
  });

  const onClick = useCallbackRef<React.MouseEventHandler<HTMLDivElement>>(() => {
    if (suppressNextClick.current) {
      suppressNextClick.current = false;
      return;
    }
    onActivate();
  });

  const onContextMenu = useCallbackRef<React.MouseEventHandler<HTMLDivElement>>((event) => {
    // Suppress the long-press callout / right-click menu only while an interaction is in flight.
    if (isDraggingRef.current || longPressTimer.current !== null) {
      event.preventDefault();
    }
  });

  React.useEffect(() => clearLongPress, [clearLongPress]);

  const positionCss = React.useMemo(() => {
    if (dragPos) return liveDragToCss(dragPos.x, dragPos.y);
    if (saved) return arrowPositionToCss(saved);
    return undefined;
  }, [dragPos, saved]);

  return {
    ref: setRef,
    positionCss,
    isDragging: dragPos !== null,
    handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel, onClick, onContextMenu },
  };
};
